/**
 * Gráfico de barras horizontais: uma linha por representante.
 * - % da meta: trilho claro até 100% (a meta), linha tracejada do ritmo e
 *   linha da supermeta (105%). O valor fica numa coluna à direita.
 * - Participação: barras sem trilho, valor na ponta da barra.
 * Cada representante tem a foto (ou as iniciais) entre o nome e a barra.
 * Passar o mouse (ou o foco do teclado) mostra a dica; clicar abre o representante.
 *
 * Lê: só as props.
 * Usado por: telas/Painel.tsx (e pela exportação de imagem).
 */
import { useId, useState } from 'react';
import type { Cores } from './cores.ts';
import { Dica } from './Dica.tsx';
import { Moldura, type Cabecalho } from './Moldura.tsx';
import { abreviarNome, caminhoBarra, limitar } from './svg.ts';

export interface ItemBarra {
  chave: string;
  rotulo: string;
  /** Na mesma unidade de `maximo` (ex.: 0,213 = 21,3%). */
  valor: number;
  /** O que aparece escrito ao lado da barra (ex.: "21,3%"). */
  texto: string;
  /** Valor zero ou sem meta: texto em cinza. */
  apagado?: boolean;
  /** Linha da equipe: barra escura, separada das outras. */
  destaque?: boolean;
  /** Iniciais e foto do representante (a linha da equipe não tem). */
  iniciais?: string;
  foto?: string | null;
  /** Linhas da dica; a primeira aparece em destaque. */
  detalhes: string[];
  aoAbrir?: () => void;
}

export interface MarcadorBarra {
  valor: number;
  rotulo: string;
  tipo: 'ritmo' | 'supermeta';
}

interface Props {
  itens: ItemBarra[];
  maximo: number;
  marcasEixo: number[];
  formatarMarca: (valor: number) => string;
  trilhoAte?: number;
  marcadores?: MarcadorBarra[];
  valorNaPonta?: boolean;
  largura: number;
  cores: Cores;
  descricao: string;
  cabecalho?: Cabecalho;
}

const ALTURA_LINHA = 30;
const ESPESSURA = 14;
const TOPO = 46;
const BASE = 8;
const SEPARACAO = 16;
const RAIO_FOTO = 11;

export function GraficoBarras(props: Props) {
  const { itens, maximo, marcasEixo, formatarMarca, trilhoAte, marcadores = [], valorNaPonta = false, largura, cores, descricao, cabecalho } = props;
  const [ativo, setAtivo] = useState<number | null>(null);
  const recorte = `foto-redonda-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;

  const margem = cabecalho ? 32 : 0;
  const larguraRotulo = Math.round(limitar(largura * 0.22, 92, 190));
  // Espaço da foto (raio 11) entre o nome e a barra, quando há representantes.
  const comFoto = itens.some((item) => item.iniciais !== undefined);
  const xFoto = margem + larguraRotulo + 8 + RAIO_FOTO;
  const x0 = margem + larguraRotulo + 12 + (comFoto ? RAIO_FOTO * 2 + 6 : 0);
  const x1 = largura - margem - (valorNaPonta ? 62 : 72);
  const x = (valor: number) => x0 + (limitar(valor, 0, maximo) / maximo) * (x1 - x0);

  let cursor = TOPO;
  const linhas = itens.map((item, indice) => {
    const separar = Boolean(item.destaque) && indice > 0;
    if (separar) cursor += SEPARACAO;
    const centro = cursor + ALTURA_LINHA / 2;
    cursor += ALTURA_LINHA;
    return { item, centro, separar };
  });
  const fundoDaGrade = cursor;
  const altura = cursor + BASE;

  const linhaAtiva = ativo === null ? null : linhas[ativo];

  return (
    <div className="grafico">
      <Moldura largura={largura} altura={altura} cores={cores} descricao={descricao} cabecalho={cabecalho}>
        <defs>
          <clipPath id={recorte} clipPathUnits="objectBoundingBox">
            <circle cx={0.5} cy={0.5} r={0.5} />
          </clipPath>
        </defs>
        {marcasEixo.map((marca) => (
          <g key={`eixo-${marca}`}>
            <line x1={x(marca)} x2={x(marca)} y1={TOPO - 6} y2={fundoDaGrade} stroke={cores.grade} strokeWidth={1} />
            <text x={x(marca)} y={TOPO - 12} fontSize={11} textAnchor="middle" fill={cores.tinta3}>
              {formatarMarca(marca)}
            </text>
          </g>
        ))}

        {linhas.map(({ item, centro, separar }, indice) => {
          const yBarra = centro - ESPESSURA / 2;
          const larguraBarra = item.valor > 0 ? Math.max(x(item.valor) - x0, 3) : 0;
          const textoX = valorNaPonta ? x0 + larguraBarra + 6 : x1 + 10;
          return (
            <g
              key={item.chave}
              className="grafico-linha"
              tabIndex={item.aoAbrir ? 0 : undefined}
              role={item.aoAbrir ? 'link' : undefined}
              aria-label={item.detalhes.join('. ')}
              onPointerEnter={() => setAtivo(indice)}
              onPointerLeave={() => setAtivo(null)}
              onFocus={() => setAtivo(indice)}
              onBlur={() => setAtivo(null)}
              onClick={item.aoAbrir}
              onKeyDown={(evento) => {
                if (evento.key === 'Enter') item.aoAbrir?.();
              }}
            >
              {separar ? (
                <line x1={margem} x2={largura - margem} y1={centro - ALTURA_LINHA / 2 - SEPARACAO / 2} y2={centro - ALTURA_LINHA / 2 - SEPARACAO / 2} stroke={cores.eixo} strokeWidth={1} />
              ) : null}
              <rect x={margem} y={centro - ALTURA_LINHA / 2} width={largura - margem * 2} height={ALTURA_LINHA} rx={6} fill={ativo === indice ? cores.grade : 'transparent'} />
              <text x={margem + larguraRotulo} y={centro} dy="0.35em" textAnchor="end" fontSize={13} fontWeight={item.destaque ? 700 : 400} fill={cores.tinta}>
                {abreviarNome(item.rotulo, larguraRotulo, 13)}
              </text>
              {item.iniciais !== undefined ? (
                <g>
                  <circle cx={xFoto} cy={centro} r={RAIO_FOTO} fill={cores.marcaSuave} />
                  <text x={xFoto} y={centro} dy="0.36em" textAnchor="middle" fontSize={9} fontWeight={800} fill={cores.marcaForte}>
                    {item.iniciais}
                  </text>
                  {item.foto ? (
                    <image href={item.foto} x={xFoto - RAIO_FOTO} y={centro - RAIO_FOTO} width={RAIO_FOTO * 2} height={RAIO_FOTO * 2} clipPath={`url(#${recorte})`} preserveAspectRatio="xMidYMid slice" />
                  ) : null}
                </g>
              ) : null}
              {trilhoAte !== undefined ? <rect x={x0} y={yBarra} width={x(trilhoAte) - x0} height={ESPESSURA} rx={4} fill={cores.trilho} /> : null}
              {larguraBarra > 0 ? <path d={caminhoBarra(x0, yBarra, larguraBarra, ESPESSURA)} fill={item.destaque ? cores.barraDestaque : cores.barra} /> : null}
              <text x={textoX} y={centro} dy="0.35em" fontSize={13} fontWeight={item.apagado ? 400 : 700} fill={item.apagado ? cores.tinta3 : cores.tinta}>
                {item.texto}
              </text>
            </g>
          );
        })}

        {marcadores
          .filter((marcador) => marcador.valor <= maximo)
          .map((marcador) => {
            const posicao = x(marcador.valor);
            const larguraRotulo = marcador.rotulo.length * 6.6;
            // Ritmo: rótulo à esquerda da linha; supermeta: à direita (assim não se encontram).
            const paraEsquerda = marcador.tipo === 'ritmo' && posicao - larguraRotulo - 4 > margem;
            return (
              <g key={marcador.tipo} pointerEvents="none">
                <line
                  x1={posicao}
                  x2={posicao}
                  y1={TOPO - 4}
                  y2={fundoDaGrade}
                  stroke={marcador.tipo === 'ritmo' ? cores.ritmo : cores.supermeta}
                  strokeWidth={marcador.tipo === 'ritmo' ? 2 : 1.5}
                  strokeDasharray={marcador.tipo === 'ritmo' ? '5 4' : undefined}
                />
                <text x={paraEsquerda ? posicao - 4 : posicao + 4} y={16} fontSize={11} fontWeight={700} textAnchor={paraEsquerda ? 'end' : 'start'} fill={cores.tinta2}>
                  {marcador.rotulo}
                </text>
              </g>
            );
          })}
      </Moldura>
      {linhaAtiva && !cabecalho ? <Dica x={x(linhaAtiva.item.valor)} y={linhaAtiva.centro + 12} larguraDisponivel={largura} linhas={linhaAtiva.item.detalhes} /> : null}
    </div>
  );
}
